import React, { useState } from 'react'
import { Formik, FormikProps } from 'formik'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Input, Button, SelectDropdown, TextboxInput } from '../../../lib/components'
import { DatePicker } from '../../../lib/components/DatePicker'
import { COUNTRIES } from '../../../lib/utils/message'
import { BasicDetailsFormValues, calculateAge } from '../../../lib/utils/onboarding'
import { getRestrictedCountryOfResidenceWarning } from '../../../lib/utils/restricted-countries'
import { basicDetailsSchema } from '../../../lib/utils/schema'

const CITIZENSHIP_COUNTRIES = COUNTRIES.filter((country) => country.value !== 'United States')

interface BasicDetailsPresenterProps {
  initialValues: BasicDetailsFormValues
  validationSchema: typeof basicDetailsSchema
  onSubmit: (values: BasicDetailsFormValues) => void | Promise<void>
  isSubmitting: boolean
}

const FieldLabel = ({
  htmlFor,
  children,
  required = false,
}: {
  htmlFor?: string
  children: React.ReactNode
  required?: boolean
}) => (
  <label htmlFor={htmlFor} className='basic-details-page__field-label'>
    {children}
    {required && <span className='basic-details-page__required'> *</span>}
  </label>
)

const SectionHeader = ({ step, title, hint }: { step: string; title: string; hint?: string }) => (
  <div
    className={`basic-details-page__section-header${
      hint ? '' : ' basic-details-page__section-header--compact'
    }`}
  >
    <span className='basic-details-page__section-icon' aria-hidden='true'>
      {step}
    </span>
    <div>
      <h3 className='basic-details-page__section-title'>{title}</h3>
      {hint ? <p className='basic-details-page__section-hint'>{hint}</p> : null}
    </div>
  </div>
)

const BasicDetailsPresenter = ({
  initialValues,
  validationSchema,
  onSubmit,
  isSubmitting,
}: BasicDetailsPresenterProps) => {
  const [callingPhoneValid, setCallingPhoneValid] = useState(true)

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik) => {
        const {
          values,
          touched,
          errors,
          dirty,
          handleSubmit,
          setFieldValue,
        }: FormikProps<BasicDetailsFormValues> = formik

        const age = calculateAge(values.dateOfBirth)
        const restrictedCountryWarning = getRestrictedCountryOfResidenceWarning(
          values.countryOfResidence,
        )

        const handlePhoneChange = (val: unknown) => {
          if (!val) {
            setFieldValue('callingPhone', '')
            setCallingPhoneValid(true)
            return
          }
          const phoneStr = String(val)
          setFieldValue('callingPhone', phoneStr)
          setCallingPhoneValid(isValidPhoneNumber(phoneStr))
        }

        return (
          <form onSubmit={handleSubmit} noValidate>
            <section
              className='basic-details-page__section'
              aria-labelledby='basic-details-heading'
            >
              <SectionHeader step='A' title='Basic details' />
              <div className='basic-details-page__field-row'>
                <div className='basic-details-page__field'>
                  <FieldLabel htmlFor='basicDetailsFirstName' required>
                    First name
                  </FieldLabel>
                  <div className='basic-details-page__control'>
                    <Input
                      name='firstName'
                      placeholder='Enter your first name'
                      type='text'
                      required
                      value={values.firstName}
                      handleChange={(e) => setFieldValue('firstName', e.target.value)}
                      dirty={dirty}
                      errors={{ firstName: errors.firstName ?? null }}
                      touched={{ firstName: !!touched.firstName }}
                      data-testid='basic-details-first-name-input'
                    />
                  </div>
                </div>
                <div className='basic-details-page__field'>
                  <FieldLabel htmlFor='basicDetailsLastName' required>
                    Last name
                  </FieldLabel>
                  <div className='basic-details-page__control'>
                    <Input
                      name='lastName'
                      placeholder='Enter your last name'
                      type='text'
                      required
                      value={values.lastName}
                      handleChange={(e) => setFieldValue('lastName', e.target.value)}
                      dirty={dirty}
                      errors={{ lastName: errors.lastName ?? null }}
                      touched={{ lastName: !!touched.lastName }}
                      data-testid='basic-details-last-name-input'
                    />
                  </div>
                </div>
                <p
                  className='basic-details-page__name-hint'
                  data-testid='basic-details-name-disclaimer'
                >
                  Please ensure the name entered matches your official documents. This name will
                  appear on all Credibila Markets official documents.
                </p>
                <div className='basic-details-page__dob-age-group basic-details-page__field--full'>
                  <div className='basic-details-page__field-row basic-details-page__field-row--identity'>
                    <div className='basic-details-page__field'>
                      <FieldLabel required>Date of birth</FieldLabel>
                      <div className='basic-details-page__control basic-details-page__control--dob'>
                        <DatePicker
                          value={values.dateOfBirth}
                          maxDate={new Date()}
                          format='DD/MM/YYYY'
                          changeHandler={(date) => {
                            setFieldValue('dateOfBirth', (date as Date) ?? null)
                          }}
                          classes={['basic-details-dob-picker']}
                        />
                      </div>
                    </div>
                    <div className='basic-details-page__field basic-details-page__field--age-col'>
                      <FieldLabel>Age</FieldLabel>
                      <div
                        className='basic-details-page__age-value'
                        data-testid='basic-details-age-display'
                        aria-live='polite'
                      >
                        {age !== null ? `${age} years` : '—'}
                      </div>
                    </div>
                    <div className='basic-details-page__field basic-details-page__phone-wrap'>
                      <FieldLabel required>Phone number</FieldLabel>
                      <div className='basic-details-page__control'>
                        <PhoneInput
                          international
                          placeholder='Enter phone number'
                          value={values.callingPhone}
                          onChange={handlePhoneChange}
                          data-testid='basic-details-calling-phone-input'
                        />
                        {!callingPhoneValid && (
                          <span className='basic-details-page__phone-error'>
                            Enter a valid phone number
                          </span>
                        )}
                        {touched.callingPhone && errors.callingPhone && (
                          <span className='basic-details-page__phone-error'>
                            {errors.callingPhone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {touched.dateOfBirth && errors.dateOfBirth && (
                    <span className='basic-details-page__phone-error'>{errors.dateOfBirth}</span>
                  )}
                </div>
                <div className='basic-details-page__field-row basic-details-page__field-row--countries basic-details-page__field--full'>
                  <div className='basic-details-page__field'>
                    <FieldLabel htmlFor='basicDetailsCountry' required>
                      Country of residence
                    </FieldLabel>
                    <div className='basic-details-page__control'>
                      <SelectDropdown
                        name='countryOfResidence'
                        id='basicDetailsCountry'
                        classes={['general-input-select']}
                        formId=''
                        list={COUNTRIES}
                        value={values.countryOfResidence}
                        handleChange={(val: string) => setFieldValue('countryOfResidence', val)}
                        dirty={dirty}
                        errors={{
                          countryOfResidence: errors.countryOfResidence ?? null,
                        }}
                        touched={{ countryOfResidence: !!touched.countryOfResidence }}
                        dataTestId='basic-details-country-select'
                      />
                      {restrictedCountryWarning && (
                        <div
                          className='basic-details-page__country-warning'
                          role='alert'
                          data-testid='basic-details-country-restricted-warning'
                        >
                          {restrictedCountryWarning}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='basic-details-page__field'>
                    <FieldLabel htmlFor='basicDetailsTaxResidency' required>
                      Tax residency
                    </FieldLabel>
                    <div className='basic-details-page__control'>
                      <SelectDropdown
                        name='taxResidency'
                        id='basicDetailsTaxResidency'
                        classes={['general-input-select']}
                        formId=''
                        list={COUNTRIES}
                        value={values.taxResidency}
                        handleChange={(val: string) => setFieldValue('taxResidency', val)}
                        dirty={dirty}
                        errors={{
                          taxResidency: errors.taxResidency ?? null,
                        }}
                        touched={{ taxResidency: !!touched.taxResidency }}
                        dataTestId='basic-details-tax-residency-select'
                      />
                    </div>
                  </div>
                  <div className='basic-details-page__field'>
                    <FieldLabel htmlFor='basicDetailsCitizenship' required>
                      Citizenship
                    </FieldLabel>
                    <div className='basic-details-page__control'>
                      <SelectDropdown
                        name='citizenship'
                        id='basicDetailsCitizenship'
                        classes={['general-input-select']}
                        formId=''
                        list={CITIZENSHIP_COUNTRIES}
                        value={values.citizenship}
                        handleChange={(val: string) => setFieldValue('citizenship', val)}
                        dirty={dirty}
                        errors={{
                          citizenship: errors.citizenship ?? null,
                        }}
                        touched={{ citizenship: !!touched.citizenship }}
                        dataTestId='basic-details-citizenship-select'
                      />
                    </div>
                  </div>
                  <div className='basic-details-page__field basic-details-page__field--full'>
                    <FieldLabel htmlFor='basicDetailsClientCv' required>
                      Tell us more about yourself
                    </FieldLabel>
                    <p
                      className='basic-details-page__field-hint'
                      data-testid='basic-details-client-cv-hint'
                    >
                      Please mention whether you are a business owner, currently employed at a
                      company, or not currently working. If a business owner or employed, please
                      mention the company name and its location.
                    </p>
                    <div
                      className='basic-details-page__control basic-details-page__control--textarea'
                      data-testid='basic-details-client-cv-input'
                    >
                      <TextboxInput
                        name='clientCv'
                        placeholder='Tell us more about yourself'
                        required
                        value={values.clientCv}
                        handleChange={(e) => setFieldValue('clientCv', e.target.value)}
                        dirty={dirty}
                        errors={{ clientCv: errors.clientCv ?? null }}
                        touched={{ clientCv: !!touched.clientCv }}
                        maxLength={500}
                      />
                    </div>
                    <div
                      className='basic-details-page__cv-example'
                      data-testid='basic-details-client-cv-example'
                    >
                      <p className='basic-details-page__cv-example-label'>Example</p>
                      <p>
                        I have over 12 years of experience in banking and wealth management, based
                        in Dubai since 2011. I started as a relationship associate before moving
                        into private banking, and later into a leadership role managing a portfolio
                        team.
                      </p>
                      <p>
                        I am currently a Partner and Co-Founder at [Company Name], a wealth advisory
                        firm based in Dubai.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div
              className='basic-details-page__disclaimer'
              role='note'
              data-testid='basic-details-disclaimer'
            >
              <strong>Important:</strong> Once you submit this form, these details cannot be
              changed. Double-check your name, date of birth, and phone number before continuing.
            </div>

            <div className='basic-details-page__actions'>
              <Button
                text={isSubmitting ? 'Saving…' : 'Save & continue'}
                classes={['auto-btn-width']}
                variant='primary-btn'
                handleClick={handleSubmit}
                isDisabled={isSubmitting}
                testId='basic-details-submit-btn'
              />
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

export { BasicDetailsPresenter }
